/**
 * What the search bar does when the shelf lookup could not be ANSWERED: the
 * database timed out or errored (`degraded` from searchPagesWithStatus), or the
 * server action itself threw.
 *
 * The lookup runs after every turn of the conversation, but it only DECIDES
 * something on the concluding one (show the shelf, open the page, or create
 * one). On every earlier turn it is decoration: the question has streamed and
 * the shopper can answer it with no shelf at all.
 *
 * It used to be fatal on every turn. Measured 2026-10-03, while a background
 * scan kept the database's search indexes out of memory: every AI turn of two
 * real searches succeeded, every lookup after them timed out, and each timeout
 * replaced the conversation with "Something went wrong". Nine turns, seven
 * error screens, no failed turn.
 *
 * So: a quiet re-run (the repeat usually lands on a warm cache), then carry on
 * without a shelf mid-conversation. Only the concluding turn may fail, and it
 * still must: an unanswerable lookup is not evidence the page is missing, so
 * it can never fall through to creating one.
 *
 * When the lookup is only PARTLY unanswered (the refined query timed out, the
 * shopper's own words found pages), the search bar shows those pages under the
 * same policy: they stand in for the shelf, and never count as "nothing found".
 */
export type DegradedShelfAction = 'retry' | 'continue' | 'fail';

/** Automatic re-runs of one lookup on a turn that asks another question. */
export const SHELF_LOOKUP_AUTO_RETRIES = 1;

/**
 * On the concluding turn, one more: its lookup decides what the shopper sees
 * next, and each attempt leaves the database's cache warmer for the following
 * one. Measured 2026-10-03 on a six-word refined query: two attempts timed out
 * at 6 s, the third answered in 0.7 s.
 */
export const CONCLUDING_SHELF_LOOKUP_AUTO_RETRIES = 2;

/**
 * Pause before the re-run. The query that timed out is still running in the
 * database and warming the cache it needed; asking again at once would only
 * queue behind it.
 */
export const SHELF_LOOKUP_RETRY_DELAY_MS = 2000;

export function onDegradedShelf({
  isDone,
  attempt,
}: {
  /** The turn concludes the conversation, so the lookup decides what happens next. */
  isDone: boolean;
  /** Automatic re-runs already made for this lookup (0 on its first failure). */
  attempt: number;
}): DegradedShelfAction {
  const retries = isDone ? CONCLUDING_SHELF_LOOKUP_AUTO_RETRIES : SHELF_LOOKUP_AUTO_RETRIES;
  if (attempt < retries) return 'retry';
  return isDone ? 'fail' : 'continue';
}
